"use client";

import type { ReactNode } from "react";

import { useLayoutEffect, useRef, useState } from "react";
import bubbleTailBase64 from "../assets/sj-helping-your-parents/bubble-tail.svg";
import bulletBase64 from "../assets/sj-helping-your-parents/bullet.svg";
import kitchenHouseBase64 from "../assets/sj-helping-your-parents/kitchen-house.png";
import snowflakeBase64 from "../assets/sj-helping-your-parents/snowflake.svg";

const bubbleTailImage = `data:image/svg+xml;base64,${bubbleTailBase64}`;
const bulletImage = `data:image/svg+xml;base64,${bulletBase64}`;
const kitchenHouseImage = `data:image/png;base64,${kitchenHouseBase64}`;
const snowflakeImage = `data:image/svg+xml;base64,${snowflakeBase64}`;

/** Figma content column width inside the 1440 desktop-final frame. */
const DESIGN_WIDTH = 1136;

const FAMILIAR_SIGNS = [
  "You’re the person everyone calls when something happens.",
  "You’re checking in more often to make sure everything is okay.",
  "Work, kids and your parents’ needs are competing for your time.",
  "You’re managing appointments, prescriptions or paperwork.",
  "Family members want to help, but you’re still coordinating everything.",
  "You’re starting to wonder how long you can keep doing all of it.",
  "You’re doing more of the grocery shopping, cooking, or errands.",
  "Your visits increasingly revolve around things that need to get done.",
] as const;

/**
 * Locks children to a fixed artboard width and uniformly scales them down
 * to fit the container — composition stays together like a scaled image.
 * Does not scale up past 1.
 *
 * Uses `zoom` (not transform) so the layout box shrinks with the content —
 * no zero-height clip before measure, and no manual height sync.
 */
function ScaleToFit({
  children,
  designWidth,
}: {
  children: ReactNode;
  designWidth: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const update = () => {
      setScale(Math.min(1, container.clientWidth / designWidth));
    };

    const observer = new ResizeObserver(update);
    observer.observe(container);
    update();

    return () => {
      observer.disconnect();
    };
  }, [designWidth]);

  return (
    <div className="w-full overflow-x-clip" ref={containerRef}>
      <div style={{ width: designWidth, zoom: scale }}>{children}</div>
    </div>
  );
}

// Desktop-final 6595:1988 artboard, locked at DESIGN_WIDTH and scaled via
// ScaleToFit. Clear-sky 6595:2028, quote 6595:2762 + polygon 6595:2764,
// and DOMUS crop 6941:3084 keep their desktop geometry at every viewport.
function FamiliarSignItem({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-2">
      {/* Base64 package asset — next/image cannot optimize data URIs. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt=""
        aria-hidden="true"
        className="size-6 shrink-0"
        height="24"
        src={bulletImage}
        width="24"
      />
      {/* 6595:2477 — Plus Jakarta Medium 18 / 28 */}
      <span className="font-medium text-md leading-7">{text}</span>
    </li>
  );
}

function RecognitionCallout() {
  return (
    <aside className="relative mt-6 w-[368px] shrink-0 rounded-[8px] bg-new-beginnings-tints-900 px-5 pt-12 pb-8 text-center">
      {/* Base64 package asset — next/image cannot optimize data URIs. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt=""
        aria-hidden="true"
        className="absolute top-0 left-1/2 size-[54px] -translate-x-1/2 -translate-y-1/2"
        height="54"
        src={snowflakeImage}
        width="54"
      />
      {/* 6595:2524 — Plus Jakarta Bold 18 / 32 */}
      <p className="font-bold text-md leading-8">
        If you recognize your family here, you don’t have to wait until
        something goes wrong to ask for help.
      </p>
    </aside>
  );
}

function QuoteBubble() {
  return (
    <div className="relative w-full">
      {/* 6595:2760 — Plus Jakarta Bold 24 / 40. Theme `text-2xl` is 48px. */}
      <p className="flex h-[72px] items-center rounded-[8px] bg-night-sky-base px-8 font-bold text-lg text-white leading-10">
        It doesn’t have to be you. At least, not all of it.
      </p>
      {/* Polygon 2: 48px from left, overlaps bottom by 11px. */}
      {/* Base64 package asset — next/image cannot optimize data URIs. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-full left-12 mt-[-11px] h-[30px] w-16"
        height="30"
        src={bubbleTailImage}
        width="64"
      />
    </div>
  );
}

function SjHelpingYourParents() {
  return (
    <section
      aria-labelledby="sj-helping-your-parents-heading"
      className="bg-campaign-shell font-jakarta text-new-beginnings-base"
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 py-12 md:px-10 lg:px-[152px] lg:pt-16 lg:pb-20">
        <ScaleToFit designWidth={DESIGN_WIDTH}>
          <div className="w-[1136px]">
            <div className="flex items-start justify-between gap-[136px]">
              <div className="w-[632px] shrink-0">
                {/* 6595:2468 — 24 / 32 normal. Theme `text-lg` is 24/32. */}
                <h2
                  className="font-normal text-lg"
                  id="sj-helping-your-parents-heading"
                >
                  Helping your parents doesn’t have to mean taking everything on
                  yourself.
                </h2>
                {/* 6595:2506 — Plus Jakarta Bold 18 / 32 */}
                <p className="mt-4 font-bold text-md leading-8">
                  Sometimes it happens so gradually, you don’t realize how much
                  you’re doing. Does any of the following sound familiar?
                </p>
                <ul className="mt-6 flex flex-col gap-[18px]">
                  {FAMILIAR_SIGNS.map((sign) => (
                    <FamiliarSignItem key={sign} text={sign} />
                  ))}
                </ul>
              </div>

              <RecognitionCallout />
            </div>

            <div className="relative mt-10">
              <div
                aria-hidden="true"
                className="absolute inset-x-0 top-[130px] h-[336px] bg-clear-sky-tints-500"
              />

              <div className="relative z-10 flex items-start justify-between gap-14 px-8">
                <div className="w-[600px] shrink-0 pt-[106px] pb-[56px]">
                  <QuoteBubble />
                  {/* 6595:2391 — Plus Jakarta Medium 18 / 28 */}
                  <div className="mt-8 space-y-7 font-medium text-md leading-7">
                    <p>
                      Every family is different. Home Instead starts by learning
                      about yours—what Mom or Dad needs, what they want to keep
                      doing, where you could use support and what matters most
                      to everyone involved.
                    </p>
                    <p>
                      Home Instead has more than 30 years of experience helping
                      older adults. Your local Home Instead team can listen,
                      answer your questions and help you understand what support
                      could look like.
                    </p>
                  </div>
                </div>

                {/* Base64 package asset — next/image cannot optimize data URIs. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt="A Home Instead Care Pro baking cookies with an older woman in a kitchen."
                  className="h-auto w-[422px] shrink-0"
                  height="434"
                  src={kitchenHouseImage}
                  width="422"
                />
              </div>
            </div>

            {/* 6595:2606 — Plus Jakarta Bold 18 / 20 */}
            <p className="mx-auto mt-4 w-[767px] text-center font-bold text-md leading-5">
              You don’t need to know exactly what kind of care your family needs
              before reaching out.
            </p>

            <div className="mt-8 flex justify-center">
              {/* 6595:2389 — Plus Jakarta Semibold 18 / 24 */}
              <a
                className="inline-flex h-16 w-[368px] items-center justify-center rounded-full bg-new-beginnings-base px-6 font-semibold text-md text-white leading-6"
                href="#leadform"
              >
                Contact us to learn more
              </a>
            </div>
          </div>
        </ScaleToFit>
      </div>
    </section>
  );
}

export { SjHelpingYourParents };
